import { useAsyncData } from '#imports'
import { computed, watch } from 'vue'

import type {
  PageCMSError,
  SupportBanner,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import { useStaffPermissions } from '@backmarket/nuxt-layer-oauth/useStaffPermissions'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { usePageParamsFromRoute } from './usePageParamsFromRoute'

type NotNull<T> = Exclude<T, null | undefined>

// checks if the prop exists and is not null/undefined in an object
// if it's the case, the object is narrowed to the type with the prop required
const hasNotNullProp = <T extends object, Key extends keyof T>(
  obj: T,
  prop: Key,
): obj is T & Required<Record<Key, NotNull<T[Key]>>> => {
  return !!obj && typeof obj === 'object' && prop in obj && !!obj[prop]
}

export function useFetchPageSupportBanner(
  blockErrors: PageCMSError[] | undefined,
) {
  const { canAccessCmsContent } = useStaffPermissions()
  const pathParams = usePageParamsFromRoute()
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  const { data, error } = useAsyncData<SupportBanner>(
    getAsyncDataKey('cms-support-banner', pathParams),
    () => {
      if (!canAccessCmsContent.value) {
        return Promise.resolve({ errors: null })
      }

      return hasNotNullProp(pathParams, 'pageCategory')
        ? $httpFetch('/content/admin/page/:pageType/:pageName/:pageCategory', {
            pathParams,
          })
        : $httpFetch('/content/admin/page/:pageType/:pageName', {
            pathParams,
          })
    },
    {
      watch: [canAccessCmsContent],
      server: false,
      lazy: true,
      dedupe: 'defer',
    },
  )

  watch(error, () => {
    if (!error.value) return

    logger.error('[CMS SUPPORT BANNER FETCH FAILED]', {
      error: error.value as Error,
      owners: ['bot-squad-content-front'],
    })
  })

  return computed(() => {
    if (!data.value || !data.value?.errors) return undefined

    const filteredErrors =
      data.value?.errors?.filter((e) => !e.message.match(/AttributeError/i)) ??
      []

    const combinedErrors = [...filteredErrors, ...(blockErrors ?? [])]

    return { ...data.value, errors: combinedErrors }
  })
}
